import React, { useEffect, useState, useRef }              from 'react'
import { useTranslation }                                  from 'react-i18next'
import {
    Button, Container, Grid,
    Paper, Stack, Typography,
}                                                          from '@mui/material'
import AlertSimpleDialog                                   from '../ui/components/alert-simple-dialog'
import { useApi }                                          from '../utils/api'
import { useNavigate, useParams }                          from 'react-router-dom'
import { useSnackbar }                                     from 'notistack'
import ShortFormControl                                    from '../ui/components/short-form-control'
import Page                                                from '../ui/components/Page'
import { BaseBreadcrumbs }                                 from '../ui/components/BaseBreadcrumbs'
import { parseCsv, readCsv }                               from '../utils/fileutils'
import {
    fDateTime, dateFromInputStringToISODate,
    INPUT_DATE_FORMAT
}                                                          from '../utils/textutils'

export default function ServiceInfoAnswerPage() {

    const api = useApi()
    const navigate = useNavigate()
    const { serviceInfoAnswerId, serviceId } = useParams()
    const { enqueueSnackbar } = useSnackbar()
    const { t } = useTranslation()
    const [service, setService] = useState(null)
    const [values, setValues] = useState({
        keyword: '',
        related: null,
        content: '',
    })
    const [valuesFromInputFile, setValuesFromInputFile] = useState([])
    const [shownDeleteDialog, setShownDeleteDialog] = useState(false)
    const fileInputRef = useRef(null)

    useEffect(() => {
        if (!isNaN(serviceId)) {
            api.service.detail(serviceId).then(data => setService(data)).catch(e => console.error(e))
        }
    }, [serviceId])
    
    useEffect(() => {
        (async () => {
            if (!isNaN(serviceInfoAnswerId)) {
                try {
                    let infoAnswer = await api.infoAnswer.detail(serviceInfoAnswerId).catch(() => ({}))
                    setValues({
                        ...infoAnswer,
                        related: infoAnswer.related ? fDateTime(infoAnswer.related, INPUT_DATE_FORMAT) : null
                    })
                } catch (e){
                    enqueueSnackbar(t('Can\'t load info answer'), { variant: "error" })
                }
            }
        })()
    }, [serviceInfoAnswerId])
    
    const handleCloseDeleteDialog = (result) => {
        setShownDeleteDialog(false)
        if (!result) {
            return
        }
        api.infoAnswer.delete(serviceInfoAnswerId).then(() => {
            enqueueSnackbar(t('Info answer deleted'), { variant: 'success' })
            navigate(`/services/${serviceId}`)
        }).catch(error => {
            enqueueSnackbar(t('Failed to delete'), { variant: 'error'} )
        })
    }

    const onSubmit = async (e) => {
        e.preventDefault()
        try {
            if (!isNaN(serviceInfoAnswerId)) {
                const data = {
                    ...values,
                    related: values.related ? dateFromInputStringToISODate(values.related) : null
                }
                const item = await api.infoAnswer.save(data)
                navigate(`/services/${serviceId}/info-answer/${item.id}`)
            } else {
                if (valuesFromInputFile.length === 0) {
                    enqueueSnackbar(t('Upload a file'), { variant: 'warning' })
                    return
                }
                await api.infoAnswer.bulkSave({ serviceId: serviceId, data: valuesFromInputFile })
                navigate(`/services/${serviceId}`)
            }
            enqueueSnackbar(t(`Info ${!isNaN(serviceInfoAnswerId) ? 'answer saved' : 'answers imported'}`), { variant: 'success' })
        } catch (e) {
            if (fileInputRef.current) {
                fileInputRef.current.value = null
            }
            setValuesFromInputFile(() => [])
            enqueueSnackbar(t('Failed to save'), { variant: "error" })
        }
    }

    const handleChange = (prop) => (event) => {
        setValues((prevValues) => ({
            ...prevValues,
            [prop]: event.target.value
        }))
    }

    const handleFileChange = async () => {
        if (!fileInputRef.current) {
            return
        }
        const selectedFile = fileInputRef.current?.files?.[0]
        if (!selectedFile || selectedFile.type !== "text/csv") {
            enqueueSnackbar(t('Invalid csv file'), { variant: 'error' })
            fileInputRef.current.value = null
            return
        }
        try {
            const csvContent = await readCsv(selectedFile)
            const parsedCsv = parseCsv(csvContent)
            for (const row of parsedCsv) {
                if (row.length !== 3) {
                    enqueueSnackbar(t('invalid-line-format', { line: row.join(',') }), { variant: 'error' })
                    fileInputRef.current.value = null
                    return
                }
                const [, related] = row
                if (related && !/^\d{4}-\d{2}-\d{2}$/.test(related)) {
                    enqueueSnackbar(t('invalid-date-format', { date: related }), { variant: 'error' })
                    fileInputRef.current.value = null
                    return
                }
            }
            const formattedCsv = parsedCsv.map(([keyword, related, content]) => ({
                keyword: keyword,
                related: related,
                content: content
            }))
            setValuesFromInputFile(formattedCsv)
        } catch (e) {
            enqueueSnackbar(t('Error reading the file'), { variant: 'error' })
        }
    }

    return (
        <Page title={t('Info answer')}>
            <Container>
                <BaseBreadcrumbs key={'breadcrumb-info-answer'}
                                 title={values?.keyword ?? t('Info answer')}
                                 items={[
                                     { title: t('Services'), href: `/services` },
                                     { title: service?.title ?? t('Service'), href: `/services/${serviceId}` }
                                 ]}/>
                <Stack direction="row" spacing={2} sx={{mb: 2}}>
                    <Typography variant="h4" gutterBottom>{t('Info answer')}</Typography>
                </Stack>
                <Container component={Paper} sx={{p: 2,}}>
                    <Grid container component={'form'} onSubmit={onSubmit} spacing={2}>
                        {!isNaN(serviceInfoAnswerId) ? (
                            <>
                                <Grid item xs={12}>
                                    <ShortFormControl
                                        name={'keyword'}
                                        title={t('Keyword')}
                                        value={values.keyword}
                                        handleChange={handleChange}/>
                                </Grid>
                                <Grid item xs={12}>
                                    <ShortFormControl
                                      name={'related'}
                                      title={t('Related')}
                                      value={values.related ?? ''}
                                      handleChange={handleChange}
                                      inputProps={{
                                          type: 'date',
                                      }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <ShortFormControl
                                        name={'content'}
                                        title={t('Content')}
                                        value={values.content}
                                        handleChange={handleChange}/>
                                </Grid>
                            </>
                        ) : (
                            <Grid item xs={12}>
                                <Stack justifyContent={'space-between'} spacing={1}>
                                    <Button variant="contained" component="label" sx={{ width: 'fit-content' }}>
                                        <input
                                            type="file"
                                            accept=".csv"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                    <Typography variant={'caption'}>{t(`helperInfoAnswerInputFile`)}</Typography>
                                </Stack>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Button type={'submit'} variant={'contained'}>{t('Save')}</Button>
                            {!isNaN(serviceInfoAnswerId) && (
                                <Button type={'button'} color={'error'} variant={'contained'} sx={{ml: 2}} onClick={() => setShownDeleteDialog(true)}>
                                    {t('Delete')}
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                    <AlertSimpleDialog
                        title={t('Remove item')}
                        message={t('delete-confirm-msg', {name: values.keyword})}
                        open={shownDeleteDialog}
                        onClose={handleCloseDeleteDialog}
                    />
                </Container>
            </Container>
        </Page>
    )
}